import api from '../lib/api';
import { TimeEntry, PaginatedResponse } from '../types';

export interface EntryFilters {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  status?: string;
}

export interface AdminEntryFilters extends EntryFilters {
  id?: string;
  userId?: string;
  projectId?: string;
}

export interface ApprovalsQueueFilters {
  page?: number;
  limit?: number;
  status?: string;
  name?: string;
  projectId?: string;
}

export interface EntryBody {
  projectId: string;
  phaseId: string;
  jobTitleId: string;
  date: string;
  fromHour: string; // 'HH:MM'
  toHour: string;   // 'HH:MM'
  notes: string;
}

export interface ApprovalEntry extends TimeEntry {
  callerRole: 'scrum' | 'lead' | 'manager';
}

export interface DangerZoneFilters {
  from?: string;
  to?: string;
  userId?: string;
  projectId?: string;
}

export interface DangerZoneUser {
  userId: string;
  name: string;
  email: string;
  total: number;
  diff: number;
}

export interface DangerZoneWeek {
  week: number;
  /** The days of the week actually examined (clipped by the start date and by today). */
  dateRange: { from: string; to: string };
  /** Hours expected in that period: 8 per Sunday-Thursday day. */
  target: number;
  users: DangerZoneUser[];
}

export interface DangerZoneResponse {
  /** The period examined; null when the request lies wholly before `startsOn`. */
  period: { from: string; to: string } | null;
  /** First day ever examined. */
  startsOn: string;
  weeks: DangerZoneWeek[];
}

export const entriesApi = {
  listMine: (params: EntryFilters) =>
    api.get<PaginatedResponse<TimeEntry>>('/api/v1/time-entries', { params }),
  create: (body: EntryBody) =>
    api.post<TimeEntry>('/api/v1/time-entries', body),
  batchCreate: (entries: EntryBody[]) =>
    api.post<TimeEntry[]>('/api/v1/time-entries/batch', { entries }),
  finalizeDay: (body: {
    date: string;
    updates?: (Partial<EntryBody> & { id: string })[];
    creates?: Omit<EntryBody, 'date'>[];
    deletes?: string[];
  }) => api.post<{ deleted: number; updated: TimeEntry[]; created: TimeEntry[] }>('/api/v1/time-entries/day', body),
  update: (id: string, body: Partial<EntryBody>) =>
    api.patch<TimeEntry>(`/api/v1/time-entries/${id}`, body),
  delete: (id: string) =>
    api.delete(`/api/v1/time-entries/${id}`),
  adminList: (params: AdminEntryFilters) =>
    api.get<PaginatedResponse<TimeEntry>>('/api/v1/time-entries/admin', { params }),
  /** Weeks (from 1 Oct 2026) in which someone's hours differ from their target; includes people who logged nothing. */
  adminDangerZone: (params: DangerZoneFilters) =>
    api.get<DangerZoneResponse>('/api/v1/time-entries/admin/danger-zone', { params }),
  /** Unpaginated — every entry matching the filters, for exports and aggregate views. */
  adminListAll: (params: Omit<AdminEntryFilters, 'page' | 'limit'>) =>
    api.get<TimeEntry[]>('/api/v1/time-entries/admin/all', { params }),

  // ── Approvals ──
  approvalsQueue: (params: ApprovalsQueueFilters) =>
    api.get<PaginatedResponse<ApprovalEntry>>('/api/v1/time-entries/approvals-queue', { params }),
  approvableProjects: () =>
    api.get<{ id: string; name: string }[]>('/api/v1/time-entries/approvals-queue/projects'),
  approve: (id: string) =>
    api.post<TimeEntry>(`/api/v1/time-entries/${id}/approve`),
  approveBatch: (entryIds: string[]) =>
    api.post<TimeEntry[]>('/api/v1/time-entries/approvals/batch', { entryIds }),
  requestEdit: (id: string, note?: string) =>
    api.post<TimeEntry>(`/api/v1/time-entries/${id}/request-edit`, { note }),
  reject: (id: string, note?: string) =>
    api.post<TimeEntry>(`/api/v1/time-entries/${id}/reject`, { note }),
};
